import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { uniqueFilename } from '@/utils/unique'
import { isSupportedImageFile } from '@/utils/supportedImageFormats'
import { setUploads, uploadFile } from '@/store/slices/uploads/actions'
import { selectUploads } from '@/store/slices/uploads/selectors'
import { setCallbackFromCreationPage } from '@/store/slices/editor/action'
import { selectCallbackFromCreationPage } from '@/store/slices/editor/selector'
import { ActionType } from '@/store/slices/editor/actionTypes'
import store, { useAppDispatch } from '@/store/store'
import { selectUser, selectUserIsSuper } from '@/store/slices/user/selectors'
import { openPremiumPrompt } from '@/services/ai/openPremiumPrompt'
import { customAmplitude } from '@/utils/customAmplitude'
import useModal from '@/hooks/useModal'
import { ShowModalType } from '@/scenes/engine/common/constants'

/**
 * The shared "pick a photo on a creation page, then continue elsewhere" flow:
 * uploads the chosen files, records the requested action, and routes to whichever
 * destination that action needs once the upload lands. Used by both the create
 * page and the feed page so they behave identically.
 */
export function useCreationPageUpload() {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { setShowModalType } = useModal()
  const uploads = useSelector(selectUploads)
  const callbackFromCreationPage = useSelector(selectCallbackFromCreationPage)

  const hasMountedRef = useRef(false)

  // Quick Create AI video: sign in, then Super, then the image picker. A
  // successful upgrade resumes straight into the picker, like the AI-video feed.
  const requestAIVideo = (pickImage: () => void) => {
    const state = store.getState()
    if (!selectUser(state)) {
      setShowModalType(ShowModalType.SIGN_IN)
      return
    }
    if (!selectUserIsSuper(state)) {
      openPremiumPrompt({ source: 'BtSubscriptionAIVideo', type: 'Super', callback: pickImage })
      return
    }
    pickImage()
  }

  // Same handoff as the AI-video feed, without a template prompt: AI Studio
  // infers the motion from the uploaded image (resolveVideoPrompt).
  const handleAIVideoFiles = async (files: FileList | File[]) => {
    const file = files[0]
    if (!file) return
    if (!isSupportedImageFile(file)) {
      setShowModalType(ShowModalType.UNSUPPORTED_FILE_TYPE)
      return
    }
    // Clear stale uploads so AI Studio boots from this file, not an earlier one.
    dispatch(setUploads([]))
    await dispatch(uploadFile({ file: new File([file], uniqueFilename(file.name)), fromCreationPage: true }))
    dispatch(setCallbackFromCreationPage({ action: ActionType.AI_VIDEO, value: { videoPrompt: null, prompt: '' } }))
  }

  useEffect(() => {
    // On arrival, drop any callback left over from an earlier flow. `uploads` also
    // survives navigation, so without this a stale pair would redirect straight
    // back out of the page the user just opened.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      if (callbackFromCreationPage) {
        dispatch(setCallbackFromCreationPage(null))
      }
      return
    }
    if (uploads.length && callbackFromCreationPage) {
      // AI Fashion Model and Photoshoot Preload route to non-Editor destinations and handle navigation themselves.
      if (
        callbackFromCreationPage.action === ActionType.AI_FASHION_MODEL ||
        callbackFromCreationPage.action === ActionType.PHOTOSHOOT_PRELOAD
      ) {
        return
      }
      // AI-video items generate a video in AI Studio; everything else opens the editor.
      history.push(callbackFromCreationPage.action === ActionType.AI_VIDEO ? '/ai-studio' : '/')
    }
  }, [uploads, callbackFromCreationPage])

  const handleUploadFile = async (file: File) => {
    try {
      const updatedFileName = uniqueFilename(file.name)
      const updatedFile = new File([file], updatedFileName)
      await dispatch(uploadFile({ file: updatedFile, fromCreationPage: true }))
      customAmplitude('bazaart.add.photo.upload')
    } catch (err) {
      console.log({ err })
    }
  }

  const handleDropFiles = (files: FileList | File[], action: ActionType) => {
    let isUnsupport = false

    for (let file of files) {
      if (isSupportedImageFile(file)) {
        handleUploadFile(file)
        dispatch(setCallbackFromCreationPage({ action }))
      } else {
        isUnsupport = true
      }
    }

    if (isUnsupport) {
      setShowModalType(ShowModalType.UNSUPPORTED_FILE_TYPE)
    }
  }

  return { handleDropFiles, requestAIVideo, handleAIVideoFiles }
}

export default useCreationPageUpload
